import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './ContrattoAttuale.css';

/**
 * Widget "Contratto Attuale" — Issue #76
 * 
 * Mostra contratto luce + gas attuale (provider, tipo offerta, scadenza, prezzo tipo).
 * Alert se scadenza < 90 giorni. Bottone → Comparatore.
 */
function ContrattoAttuale() {
  const [contratto, setContratto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchContratto();
  }, []);
  
  const fetchContratto = async () => {
    try {
      // FIX #100: Use correct API path /api/bollette/contratto-attuale
      // Backend route mounted at /api/bollette/:contratto-attuale
      const res = await api.get('/bollette/contratto-attuale');
      setContratto(res.data);
    } catch (err) {
      console.error('[ContrattoAttuale] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Calcola giorni rimanenti a scadenza (supporta ISO YYYY-MM-DD + DD/MM/YYYY)
  const calcGiorniScadenza = (scadenzaStr) => {
    if (!scadenzaStr) return null;
    
    let scadenza;
    
    // Check ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(scadenzaStr)) {
      scadenza = new Date(scadenzaStr);
    } else {
      // Parse DD/MM/YYYY (o varianti con separatori diversi)
      const parts = scadenzaStr.split(/[-./]/);
      if (parts.length !== 3) return null;
      const [day, month, year] = parts.map(Number);
      scadenza = new Date(year, month - 1, day);
    }
    
    // Verifica validità data
    if (isNaN(scadenza.getTime())) return null;
    
    const oggi = new Date();
    const diff = scadenza.getTime() - oggi.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };
  
  // Formatta data per display (converte ISO → DD/MM/YYYY se necessario)
  const formatScadenza = (dateStr) => {
    if (!dateStr) return null;
    
    // Se già DD/MM/YYYY, return as-is
    if (/^\d{2}[-.\/]\d{2}[-.\/]\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Se ISO, converti
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    
    return dateStr; // Fallback
  };
  
  if (loading) {
    return (
      <div className="contratto-attuale">
        <p>Caricamento contratto...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="contratto-attuale error">
        <p>⚠️ Errore caricamento: {error}</p>
      </div>
    );
  }
  
  // Se nessuna bolletta caricata, mostra placeholder
  if (!contratto || (!contratto.luce && !contratto.gas)) {
    return (
      <div className="contratto-attuale empty">
        <h2>⚡ Contratto Attuale</h2>
        <p style={{ color: '#666', marginBottom: '15px' }}>
          Carica una bolletta per visualizzare i dettagli del tuo contratto luce/gas.
        </p>
        <button 
          onClick={() => document.querySelector('.upload-form')?.scrollIntoView({ behavior: 'smooth' })} 
          className="btn btn-primary"
        >
          📤 Carica Bolletta
        </button>
      </div>
    );
  }
  
  const giorniLuce = contratto.luce?.scadenza ? calcGiorniScadenza(contratto.luce.scadenza) : null;
  const giorniGas = contratto.gas?.scadenza ? calcGiorniScadenza(contratto.gas.scadenza) : null;
  
  const showAlertLuce = giorniLuce !== null && giorniLuce < 90 && giorniLuce >= 0;
  const showAlertGas = giorniGas !== null && giorniGas < 90 && giorniGas >= 0;
  
  return (
    <div className="contratto-attuale">
      <h2>⚡ Contratto Attuale</h2>
      
      <div className="contratti-grid">
        {/* Contratto Luce */}
        {contratto.luce && (
          <div className="contratto-card luce">
            <h3>💡 Luce</h3>
            <div className="contratto-info">
              <p>
                <strong>Provider:</strong> {contratto.luce.provider}
              </p>
              <p>
                <strong>Tipo:</strong>{' '}
                {contratto.luce.tipo_offerta ? (
                  <>
                    {contratto.luce.tipo_offerta}
                    {contratto.luce.prezzo_tipo && (
                      <> - Prezzo <span className={`badge ${contratto.luce.prezzo_tipo}`}>
                        {contratto.luce.prezzo_tipo}
                      </span></>
                    )}
                  </>
                ) : (
                  <span className="placeholder">Non disponibile</span>
                )}
              </p>
              <p>
                <strong>Scadenza:</strong>{' '}
                {contratto.luce.scadenza ? (
                  formatScadenza(contratto.luce.scadenza)
                ) : (
                  <span className="placeholder">Non disponibile</span>
                )}
              </p>
            </div>
            
            {showAlertLuce && (
              <div className="alert alert-warning">
                ⚠️ Contratto in scadenza tra <strong>{giorniLuce}</strong> giorni!
              </div>
            )}
          </div>
        )}
        
        {/* Contratto Gas */}
        {contratto.gas && (
          <div className="contratto-card gas">
            <h3>🔥 Gas</h3>
            <div className="contratto-info">
              <p>
                <strong>Provider:</strong> {contratto.gas.provider}
              </p>
              <p>
                <strong>Tipo:</strong>{' '}
                {contratto.gas.tipo_offerta ? (
                  <>
                    {contratto.gas.tipo_offerta}
                    {contratto.gas.prezzo_tipo && (
                      <> - Prezzo <span className={`badge ${contratto.gas.prezzo_tipo}`}>
                        {contratto.gas.prezzo_tipo}
                      </span></>
                    )}
                  </>
                ) : (
                  <span className="placeholder">Non disponibile</span>
                )}
              </p>
              <p>
                <strong>Scadenza:</strong>{' '}
                {contratto.gas.scadenza ? (
                  formatScadenza(contratto.gas.scadenza)
                ) : (
                  <span className="placeholder">Non disponibile</span>
                )}
              </p>
            </div>
            
            {showAlertGas && (
              <div className="alert alert-warning">
                ⚠️ Contratto in scadenza tra <strong>{giorniGas}</strong> giorni!
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="contratto-actions">
        <button onClick={() => window.location.hash = 'comparatore'} className="btn btn-primary">
          🔍 Confronta Offerte
        </button>
      </div>
    </div>
  );
}

export default ContrattoAttuale;
